import { useState, useEffect, useCallback, useRef, Fragment, type ReactNode } from "react";
import { AdminMenuImages } from "@/components/AdminMenuImages";
import { PrinterManagementPanel } from "@/components/PrinterManagementPanel";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect, useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { RichTextEditor } from "@/components/RichTextEditor";
import { CheckCircle, XCircle, Store, ExternalLink, Settings, Search, MapPin, ArrowRightLeft, UserPlus, X, Trash2, Zap, Mail, Copy, Check, Monitor, ChevronRight, FileText } from "lucide-react";

const RUSTDESK_SERVER = "ayrshirehub.co.uk";
const RUSTDESK_KEY = "qgF1EQZwL+LfBIu5a4lKUuOw4Lto1bcLKtZN57QS6m0=";
const RUSTDESK_PASSWORD = "Hub990@@@";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

interface TermsAcceptanceRow {
  id: number; userId: number; termsVersion: string; signedName: string;
  signatureDataUrl: string | null; ipAddress: string | null; acceptedAt: string;
  signerName: string | null; signerEmail: string | null;
}
function TermsAcceptancePanel({ storeId, storeName }: { storeId: number; storeName: string }) {
  const [data, setData] = useState<{ currentVersion: string | null; acceptedCurrent: boolean; latest: TermsAcceptanceRow | null; history: TermsAcceptanceRow[] } | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    fetch(`/api/admin/order-stores/${storeId}/terms-acceptance`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [storeId]);

  if (loading) return <div className="text-sm text-slate-400">Loading terms status…</div>;
  if (!data) return <div className="text-sm text-rose-600">Could not load terms status.</div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <FileText size={15} className="text-slate-500" />
        <span className="font-semibold text-slate-800">Terms &amp; Conditions</span>
        {data.acceptedCurrent
          ? <span className="text-xs font-semibold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Signed (v{data.currentVersion})</span>
          : <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Not signed{data.currentVersion ? ` (v${data.currentVersion})` : ""}</span>}
      </div>

      {data.history.length === 0 ? (
        <p className="text-sm text-slate-500">{storeName} has not signed any version of the partner terms yet.</p>
      ) : (
        <div className="space-y-3">
          {data.history.map(h => (
            <div key={h.id} className="rounded-lg border border-slate-200 bg-white p-3 flex flex-col sm:flex-row gap-3 sm:items-center">
              <div className="flex-1 text-sm">
                <div className="font-semibold text-slate-800">{h.signedName}
                  <span className="ml-2 text-xs font-normal text-slate-400">v{h.termsVersion}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Signed by {h.signerName ?? "user"} {h.signerEmail ? `(${h.signerEmail})` : ""} · {new Date(h.acceptedAt).toLocaleString("en-GB")}
                  {h.ipAddress ? ` · IP ${h.ipAddress}` : ""}
                </div>
              </div>
              {h.signatureDataUrl && (
                <img src={h.signatureDataUrl} alt="signature" className="h-16 w-auto max-w-[240px] rounded border border-slate-200 bg-white self-start" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  }
  return (
    <div className="flex items-center justify-between bg-slate-100 rounded-lg px-3 py-2">
      <div>
        <p className="text-xs text-slate-400 font-medium">{label}</p>
        <p className="text-sm font-mono text-slate-800 font-semibold">{value}</p>
      </div>
      <button onClick={copy} className="text-slate-400 hover:text-slate-700 ml-3 flex-shrink-0" title="Copy">
        {copied ? <Check size={15} className="text-green-600" /> : <Copy size={15} />}
      </button>
    </div>
  );
}

function RemoteControlModal({ store, onClose }: { store: StoreRow; onClose: () => void }) {
  const webClientUrl = `/remote/`;
  const appUrl = `rustdesk://connect/${store.rustdeskId ?? ""}`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-slate-800 flex items-center gap-2"><Monitor size={18} /> Remote Control — {store.name}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        <a href={webClientUrl} target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full bg-primary text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors">
          <Monitor size={16} /> Open Web Remote
        </a>

        <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs text-blue-700">
          <p className="font-semibold mb-1">Once the web client opens:</p>
          <p>Enter the Device ID below and password <span className="font-mono font-bold">{RUSTDESK_PASSWORD}</span> to connect.</p>
        </div>

        <div className="space-y-2">
          <CopyField label="Device ID — paste into web client" value={store.rustdeskId ?? ""} />
          <CopyField label="Password" value={RUSTDESK_PASSWORD} />
        </div>

        <div className="border-t border-slate-100 pt-3">
          <a href={appUrl}
            className="flex items-center justify-center gap-2 w-full border border-slate-200 text-slate-600 rounded-lg py-2 text-xs font-medium hover:bg-slate-50 transition-colors">
            <Monitor size={12} /> Or open in RustDesk desktop app
          </a>
        </div>

        <p className="text-xs text-slate-400 text-center">The tablet must have RustDesk open and connected.</p>
      </div>
    </div>
  );
}

interface StoreRow {
  id: number;
  name: string;
  category: string;
  categories: string[];
  town: string;
  address: string;
  phone: string | null;
  email: string | null;
  approved: boolean;
  isActive: boolean;
  stripeOnboardingComplete: boolean;
  stripeAccountId: string | null;
  acceptsCash: boolean;
  offersDelivery: boolean;
  offersCollection: boolean;
  weeklyFeePence: number | null;
  platformFeePence: number | null;
  hubDriverFeePence: number | null;
  driverBonusPence: number | null;
  multiStoreBonusPence: number | null;
  externalDriverFeePence: number;
  useHubDrivers: boolean;
  recurringDiscountPercent: number;
  menuUpliftPercent: number;
  stripeFeeResponsibility: string;
  storeLatitude: number | null;
  storeLongitude: number | null;
  createdAt: string;
  slug: string | null;
  ownerId: number | null;
  ownerName: string | null;
  ownerEmail: string | null;
  multiStoreEnabled: boolean;
  multiStoreRole: "ANCHOR_AND_COMPANION" | "COMPANION_ONLY" | "DISABLED";
  rustdeskId: string | null;
  subcategoriesEnabled: boolean;
}

interface UserSearchResult {
  id: number;
  name: string;
  email: string;
  role: string;
}

function TransferOwnerPanel({ store, onTransferred }: { store: StoreRow; onTransferred: (newOwnerId: number, name: string, email: string) => void }) {
  const { toast } = useToast();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const token = localStorage.getItem("ayrshire_token");
        const res = await fetch(`/api/admin/users/search?q=${encodeURIComponent(query)}`, {
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        const data = await res.json() as { users: UserSearchResult[] };
        setResults(data.users ?? []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
  }, [query]);

  async function handleTransfer(user: UserSearchResult) {
    setTransferring(true);
    try {
      const token = localStorage.getItem("ayrshire_token");
      const res = await fetch(`/api/admin/order-stores/${store.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ ownerId: user.id }),
      });
      if (!res.ok) throw new Error("Failed");
      toast({ title: `${store.name} transferred to ${user.name}` });
      onTransferred(user.id, user.name, user.email);
      setQuery("");
      setResults([]);
    } catch {
      toast({ title: "Transfer failed", variant: "destructive" });
    } finally {
      setTransferring(false);
    }
  }

  return (
    <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-2">
      <div className="text-xs text-slate-500">
        <span className="font-semibold">Current owner:</span>{" "}
        {store.ownerName ? `${store.ownerName} (${store.ownerEmail ?? "no email"})` : <span className="italic">Unknown (ID {store.ownerId})</span>}
      </div>
      <div className="relative">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search new owner by name or email…"
          className="h-7 text-xs pl-7"
        />
      </div>
      {searching && <p className="text-xs text-slate-400">Searching…</p>}
      {results.length > 0 && (
        <ul className="border border-slate-200 rounded-md bg-white divide-y divide-slate-100 max-h-40 overflow-y-auto">
          {results.map(u => (
            <li key={u.id} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-800 truncate">{u.name}</p>
                <p className="text-[10px] text-slate-500 truncate">{u.email} · {u.role}</p>
              </div>
              <Button
                size="sm"
                disabled={transferring || u.id === store.ownerId}
                onClick={() => void handleTransfer(u)}
                className="h-6 text-[10px] px-2 ml-2 shrink-0 bg-primary hover:bg-primary/90"
              >
                {u.id === store.ownerId ? "Current" : "Transfer"}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {!searching && query.trim().length >= 2 && results.length === 0 && (
        <p className="text-xs text-slate-400">No users found.</p>
      )}
    </div>
  );
}

function CreateShopPanel({ stores, onCreated, onClose }: { stores: StoreRow[]; onCreated: () => void; onClose: () => void }) {
  const { toast } = useToast();
  const [mode, setMode] = useState<"create" | "link">("create");
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  // create mode
  const [storeName, setStoreName] = useState("");
  const [town, setTown] = useState("");
  // link mode
  const [storeQuery, setStoreQuery] = useState("");
  const [selectedStore, setSelectedStore] = useState<StoreRow | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const filteredStores = storeQuery.trim().length >= 1
    ? stores.filter(s => s.name.toLowerCase().includes(storeQuery.toLowerCase())).slice(0, 8)
    : [];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!ownerName.trim() || !ownerEmail.trim()) {
      toast({ title: "Owner name and email are required", variant: "destructive" }); return;
    }
    if (mode === "create" && !storeName.trim()) {
      toast({ title: "Shop name is required", variant: "destructive" }); return;
    }
    if (mode === "link" && !selectedStore) {
      toast({ title: "Please select an existing store", variant: "destructive" }); return;
    }
    setSubmitting(true);
    try {
      const body = mode === "create"
        ? { mode: "create", ownerName, ownerEmail, storeName, town }
        : { mode: "link", ownerName, ownerEmail, existingStoreId: selectedStore!.id };
      const res = await fetch("/api/admin/order-stores/create-with-account", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json() as { storeId?: number; error?: string };
      if (!res.ok) {
        toast({ title: data.error ?? "Failed", variant: "destructive" }); return;
      }
      toast({ title: `Account created & welcome email sent to ${ownerEmail}` });
      onCreated();
      onClose();
    } catch {
      toast({ title: "Something went wrong", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-foreground">Create Shop Account</h2>
            <p className="text-xs text-slate-500 mt-0.5">Creates a login and emails credentials + Stripe setup link</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Mode toggle */}
        <div className="flex rounded-xl border border-slate-200 overflow-hidden">
          {(["create", "link"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => { setMode(m); setSelectedStore(null); setStoreQuery(""); }}
              className="flex-1 py-2 text-sm font-medium transition-colors"
              style={{
                background: mode === m ? "#1e3a5f" : "var(--color-card)",
                color: mode === m ? "#fff" : "#64748b",
              }}
            >
              {m === "create" ? "Create New Store" : "Link to Existing Store"}
            </button>
          ))}
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3">
          {/* Owner fields — always shown */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Owner Name *</label>
            <Input value={ownerName} onChange={e => setOwnerName(e.target.value)} placeholder="e.g. Jane Smith" required />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Owner Email *</label>
            <Input type="email" value={ownerEmail} onChange={e => setOwnerEmail(e.target.value)} placeholder="e.g. jane@example.com" required />
          </div>

          {/* Create mode extras */}
          {mode === "create" && (
            <>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Shop Name *</label>
                <Input value={storeName} onChange={e => setStoreName(e.target.value)} placeholder="e.g. Ducky's Kitchen" required />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Town</label>
                <Input value={town} onChange={e => setTown(e.target.value)} placeholder="e.g. Kilmarnock" />
              </div>
            </>
          )}

          {/* Link mode store picker */}
          {mode === "link" && (
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Existing Store *</label>
              {selectedStore ? (
                <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-xl px-3 py-2">
                  <Store size={14} className="text-primary shrink-0" />
                  <span className="text-sm font-medium text-primary flex-1">{selectedStore.name} <span className="font-normal text-slate-400">— {selectedStore.town}</span></span>
                  <button type="button" onClick={() => { setSelectedStore(null); setStoreQuery(""); }} className="text-slate-400 hover:text-slate-600">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2 focus-within:border-primary transition-colors">
                    <Search size={14} className="text-slate-400 shrink-0" />
                    <input
                      value={storeQuery}
                      onChange={e => setStoreQuery(e.target.value)}
                      placeholder="Search stores…"
                      className="flex-1 text-sm outline-none bg-transparent"
                    />
                  </div>
                  {filteredStores.length > 0 && (
                    <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                      {filteredStores.map(s => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => { setSelectedStore(s); setStoreQuery(""); }}
                          className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0"
                        >
                          <span className="font-medium text-slate-800">{s.name}</span>
                          <span className="text-slate-400 text-xs ml-2">{s.town}</span>
                          {s.ownerName && <span className="text-slate-400 text-xs ml-2">· currently {s.ownerName}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl p-3">
            <p className="text-xs text-amber-700">
              {mode === "create"
                ? "Creates a new shop listing (inactive, pending your approval) and emails login details + Stripe setup link."
                : "Links the selected store to the new account. The owner will receive login details + a Stripe setup link."}
            </p>
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90" disabled={submitting}>
              {submitting ? "Creating…" : "Create & Send Email"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CoordCell({ store, onSave }: { store: StoreRow; onSave: (lat: number | null, lng: number | null) => void }) {
  const [editing, setEditing] = useState(false);
  const [lat, setLat] = useState(store.storeLatitude?.toString() ?? "");
  const [lng, setLng] = useState(store.storeLongitude?.toString() ?? "");
  const hasCoords = store.storeLatitude != null && store.storeLongitude != null;

  function commit() {
    setEditing(false);
    const parsedLat = lat.trim() ? parseFloat(lat) : null;
    const parsedLng = lng.trim() ? parseFloat(lng) : null;
    onSave(parsedLat, parsedLng);
  }

  if (editing) {
    return (
      <div className="space-y-1 min-w-[160px]">
        <input
          type="number" step="any" value={lat} onChange={(e) => setLat(e.target.value)}
          onFocus={(e) => e.target.select()}
          placeholder="Latitude e.g. 55.4628"
          className="w-full text-xs border border-primary rounded px-1.5 py-0.5 outline-none"
        />
        <input
          type="number" step="any" value={lng} onChange={(e) => setLng(e.target.value)}
          onFocus={(e) => e.target.select()}
          placeholder="Longitude e.g. -4.6289"
          className="w-full text-xs border border-primary rounded px-1.5 py-0.5 outline-none"
        />
        <div className="flex gap-1 pt-0.5">
          <button onClick={commit} className="text-xs bg-primary text-white rounded px-2 py-0.5 hover:bg-primary/90">Save</button>
          <button onClick={() => setEditing(false)} className="text-xs text-slate-400 hover:text-slate-600 px-1">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {hasCoords ? (
        <div className="text-xs text-slate-600 font-mono">
          {store.storeLatitude!.toFixed(4)}, {store.storeLongitude!.toFixed(4)}
        </div>
      ) : (
        <span className="text-xs text-amber-600 italic">Not set</span>
      )}
      <div className="flex items-center gap-2">
        <button onClick={() => setEditing(true)} className="text-xs text-primary hover:underline flex items-center gap-0.5">
          <MapPin size={10} /> {hasCoords ? "Edit" : "Add"}
        </button>
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(store.address + ", " + store.town)}`}
          target="_blank" rel="noopener noreferrer"
          className="text-xs text-slate-400 hover:text-slate-600 underline"
        >
          Google Maps
        </a>
      </div>
    </div>
  );
}

function WeeklyFeeCell({ store, onSave }: { store: StoreRow; onSave: (v: number | null) => void }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(store.weeklyFeePence != null ? (store.weeklyFeePence / 100).toFixed(2) : "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  function commit() {
    setEditing(false);
    const trimmed = val.trim();
    if (trimmed === "") { onSave(null); return; }
    const pounds = parseFloat(trimmed);
    if (!isNaN(pounds) && pounds >= 0) onSave(Math.round(pounds * 100));
    else setVal(store.weeklyFeePence != null ? (store.weeklyFeePence / 100).toFixed(2) : "");
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-slate-400 text-xs">£</span>
        <input
          ref={inputRef}
          type="number"
          min={0}
          step={0.01}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
          className="w-20 text-sm border border-primary rounded px-1.5 py-0.5 outline-none"
        />
      </div>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="text-sm text-slate-700 hover:text-primary hover:underline text-left"
      title="Click to edit"
    >
      {store.weeklyFeePence != null ? `£${(store.weeklyFeePence / 100).toFixed(2)}` : <span className="text-slate-400 italic">default</span>}
    </button>
  );
}

const DURATIONS = [1, 2, 3, 6, 12, 24];

function BoostModal({ store, allTowns, onClose, onBoostCreated }: {
  store: StoreRow;
  allTowns: string[];
  onClose: () => void;
  onBoostCreated: () => void;
}) {
  const { toast } = useToast();
  const [selectedTowns, setSelectedTowns] = useState<string[]>(store.town ? [store.town] : []);
  const [durationHours, setDurationHours] = useState(1);
  const [priceGbp, setPriceGbp] = useState("0.00");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  function toggleTown(t: string) {
    setSelectedTowns(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (selectedTowns.length === 0) {
      toast({ title: "Select at least one town", variant: "destructive" }); return;
    }
    const pricePence = Math.round(parseFloat(priceGbp || "0") * 100);
    if (isNaN(pricePence) || pricePence < 0) {
      toast({ title: "Invalid price", variant: "destructive" }); return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/admin/order-stores/${store.id}/boost`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ towns: selectedTowns, durationHours, pricePence, notes: notes.trim() || undefined }),
      });
      if (!res.ok) {
        const d = await res.json() as { error?: string };
        toast({ title: d.error ?? "Failed to create boost", variant: "destructive" }); return;
      }
      toast({ title: `${store.name} boosted for ${durationHours}h` });
      onBoostCreated();
      onClose();
    } catch {
      toast({ title: "Something went wrong", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
              <Zap size={18} className="text-amber-500" /> Boost Store
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">{store.name} will appear at the top of listings for selected towns</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">Towns</label>
            <div className="flex flex-wrap gap-2">
              {allTowns.map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleTown(t)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    selectedTowns.includes(t)
                      ? "bg-amber-500 border-amber-500 text-white"
                      : "bg-white border-slate-200 text-slate-600 hover:border-amber-300"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            {selectedTowns.length === 0 && (
              <p className="text-xs text-red-500 mt-1">Select at least one town</p>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">Duration</label>
            <div className="flex gap-2 flex-wrap">
              {DURATIONS.map(d => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDurationHours(d)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    durationHours === d
                      ? "bg-primary border-primary text-white"
                      : "bg-white border-slate-200 text-slate-600 hover:border-primary/50"
                  }`}
                >
                  {d}h
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">
              Price <span className="text-slate-400 font-normal">(deducted from card payments — set £0 for free)</span>
            </label>
            <div className="flex items-center border border-slate-200 rounded-xl px-3 py-2 focus-within:border-amber-400 transition-colors">
              <span className="text-slate-500 mr-1">£</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={priceGbp}
                onChange={e => setPriceGbp(e.target.value)}
                onFocus={e => e.target.select()}
                className="flex-1 text-sm outline-none bg-transparent"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Notes <span className="font-normal text-slate-400">(optional)</span></label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. Paid by card on 28 July"
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-amber-400 transition-colors resize-none"
            />
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
            Boost starts immediately and expires after {durationHours}h.
            {parseFloat(priceGbp || "0") > 0 && ` £${parseFloat(priceGbp).toFixed(2)} will be recorded as outstanding and deducted from ${store.name}'s next card payment settlement.`}
          </div>

          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600 text-white" disabled={submitting || selectedTowns.length === 0}>
              {submitting ? "Boosting…" : <><Zap size={14} className="mr-1" /> Boost Now</>}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MultiStorePanel({ store, onSaved, onClose }: { store: StoreRow; onSaved: (updates: Partial<StoreRow>) => void; onClose: () => void }) {
  const { toast } = useToast();
  const [enabled, setEnabled] = useState(store.multiStoreEnabled ?? false);
  const [role, setRole] = useState<StoreRow["multiStoreRole"]>(store.multiStoreRole ?? "DISABLED");
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      const res = await fetch(`/api/admin/stores/${store.id}/multi-store`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ multiStoreEnabled: enabled, multiStoreRole: role }),
      });
      if (!res.ok) {
        const d = await res.json() as { error?: string };
        toast({ title: d.error ?? "Failed to save multi-store settings", variant: "destructive" }); return;
      }
      onSaved({ multiStoreEnabled: enabled, multiStoreRole: role });
      toast({ title: "Multi-store settings saved" });
      onClose();
    } catch {
      toast({ title: "Something went wrong", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-4 space-y-4 bg-slate-50 rounded-xl border border-slate-200">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-slate-700">Multi-Store Settings — {store.name}</p>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
      </div>
      <div className="flex items-center gap-3">
        <input
          id={`ms-enabled-${store.id}`}
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
        />
        <label htmlFor={`ms-enabled-${store.id}`} className="text-sm text-slate-700 font-medium">
          Multi-store ordering enabled
        </label>
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Role</label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as StoreRow["multiStoreRole"])}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-primary transition-colors bg-white"
        >
          <option value="DISABLED">Disabled — not part of multi-store</option>
          <option value="ANCHOR_AND_COMPANION">Anchor &amp; Companion — can be either anchor or companion</option>
          <option value="COMPANION_ONLY">Companion Only — driver collects here as a stop, not the primary pickup</option>
        </select>
      </div>
      <div className="flex gap-2 pt-1">
        <Button type="button" variant="outline" className="flex-1 h-8 text-xs" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button type="button" className="flex-1 h-8 text-xs bg-primary hover:bg-primary/90" onClick={() => void handleSave()} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function BroadcastEmailModal({ emails, onClose }: { emails: string[]; onClose: () => void }) {
  const { toast } = useToast();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  const emailList = emails.join(", ");
  // body is now rich-text HTML from the editor; check for real text/images before allowing send.
  const bodyIsEmpty = !body.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim() && !/<img\b/i.test(body);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(emailList);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Could not copy to clipboard", variant: "destructive" });
    }
  }

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!subject.trim() || bodyIsEmpty) {
      toast({ title: "Subject and message are required", variant: "destructive" }); return;
    }
    setSending(true);
    try {
      const res = await fetch("/api/admin/order-stores/email-all", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ subject: subject.trim(), body: body.trim() }),
      });
      if (!res.ok) {
        const d = await res.json() as { error?: string };
        toast({ title: d.error ?? "Failed to send emails", variant: "destructive" }); return;
      }
      const { sent } = await res.json() as { sent: number };
      toast({ title: `Email sent to ${sent} store owners` });
      onClose();
    } catch {
      toast({ title: "Something went wrong", variant: "destructive" });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
              <Mail size={18} className="text-primary" /> Email All Store Owners
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">{emails.length} active owners with email addresses</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Email Addresses</label>
            <button
              type="button"
              onClick={() => void handleCopy()}
              className="flex items-center gap-1 text-xs text-primary hover:underline"
            >
              {copied ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
              {copied ? "Copied!" : "Copy all"}
            </button>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-600 font-mono max-h-24 overflow-y-auto break-all leading-relaxed select-all">
            {emailList || <span className="text-slate-400 italic">No active store owners with email addresses</span>}
          </div>
        </div>

        <form onSubmit={(e) => void handleSend(e)} className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Important update from Ayrshire Hub"
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-primary transition-colors"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Message</label>
            <RichTextEditor
              value={body}
              onChange={setBody}
              placeholder="Write your message here — use bold, underline, headings, lists, links…"
            />
            <p className="text-xs text-slate-400 mt-1">Formatting (bold, underline, headings, lists, links, images) is preserved in the email.</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700">
            Email will be sent BCC to all {emails.length} active store owners. Each recipient sees only their own address.
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose} disabled={sending}>Cancel</Button>
            <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90 text-white gap-1.5" disabled={sending || emails.length === 0 || !subject.trim() || bodyIsEmpty}>
              {sending ? "Sending…" : <><Mail size={14} /> Send to {emails.length} owners</>}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function InlineSlugEditor({ store, onSave }: { store: StoreRow; onSave: (slug: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(store.slug ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    const cleaned = value.trim();
    if (!cleaned) { setError("Slug cannot be empty"); return; }
    if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(cleaned)) {
      setError("Lowercase letters, numbers, hyphens only"); return;
    }
    setSaving(true); setError(null);
    try {
      const res = await fetch(`/api/admin/order-stores/${store.id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ slug: cleaned }),
      });
      const d = await res.json() as { error?: string };
      if (!res.ok) { setError(d.error ?? "Failed"); return; }
      onSave(cleaned);
      setEditing(false);
    } catch { setError("Network error"); }
    finally { setSaving(false); }
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-1 mt-0.5">
        <span className={`text-xs font-mono ${store.slug ? "text-slate-500" : "text-amber-500 italic"}`}>
          {store.slug ? `/${store.slug}` : "no slug"}
        </span>
        <button onClick={() => { setValue(store.slug ?? ""); setEditing(true); setError(null); }}
          className="text-slate-300 hover:text-primary ml-0.5" title="Edit slug">
          <Settings size={10} />
        </button>
      </div>
    );
  }

  return (
    <div className="mt-1 space-y-1">
      <div className="flex items-center gap-1">
        <span className="text-xs text-slate-400">/</span>
        <input
          autoFocus
          value={value}
          onChange={e => { setValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")); setError(null); }}
          onKeyDown={e => { if (e.key === "Enter") void save(); if (e.key === "Escape") setEditing(false); }}
          className="text-xs font-mono border border-slate-200 rounded px-1.5 py-0.5 w-36 outline-none focus:border-primary"
          placeholder="store-slug"
        />
        <button onClick={() => void save()} disabled={saving}
          className="text-xs text-white bg-primary rounded px-1.5 py-0.5 disabled:opacity-50">
          {saving ? "…" : "Save"}
        </button>
        <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-600"><X size={12} /></button>
      </div>
      {error && <p className="text-[10px] text-red-500">{error}</p>}
    </div>
  );
}

// One modal per store consolidating everything that used to be crammed inline in the table:
// location, all fees/pricing, device, feature toggles and the danger actions.
function StoreSettingsModal({ store, onClose, patch, onBoost, onMultiStore, onTransfer, onRemote, onDelete }: {
  store: StoreRow;
  onClose: () => void;
  patch: (id: number, updates: Partial<StoreRow>) => void | Promise<void>;
  onBoost: () => void;
  onMultiStore: () => void;
  onTransfer: () => void;
  onRemote: () => void;
  onDelete: () => void;
}) {
  const Section = ({ title, children }: { title: string; children: ReactNode }) => (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{title}</p>
      {children}
    </div>
  );
  const feeRow = (label: string, value: number | null, onSave: (v: number | null) => void) => (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-slate-600">{label}</span>
      <WeeklyFeeCell store={{ ...store, weeklyFeePence: value }} onSave={onSave} />
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 space-y-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between sticky -top-6 bg-white pt-1 pb-2 -mt-1 z-10">
          <h2 className="font-bold text-slate-800 flex items-center gap-2"><Settings size={18} /> {store.name}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        <AdminMenuImages key={`shop-${store.id}`} kind="shop" targetId={store.id} name={store.name} />

        <Section title="Location">
          <CoordCell store={store} onSave={(lat, lng) => void patch(store.id, { storeLatitude: lat, storeLongitude: lng } as Partial<StoreRow>)} />
        </Section>

        <Section title="Fees & pricing">
          <div className="space-y-2">
            {feeRow("Weekly fee", store.weeklyFeePence, (v) => void patch(store.id, { weeklyFeePence: v }))}
            {feeRow("Order fee", store.platformFeePence, (v) => void patch(store.id, { platformFeePence: v }))}
            {feeRow("Hub driver fee", store.hubDriverFeePence, (v) => void patch(store.id, { hubDriverFeePence: v }))}
            {feeRow("Fuel supplement", store.driverBonusPence, (v) => void patch(store.id, { driverBonusPence: v }))}
            {feeRow("Multi-order fuel", store.multiStoreBonusPence, (v) => void patch(store.id, { multiStoreBonusPence: v }))}
            {feeRow("External driver request", store.externalDriverFeePence, (v) => void patch(store.id, { externalDriverFeePence: v ?? 150 }))}
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-slate-600">Recurring discount</span>
              <div className="flex items-center gap-1">
                <input type="number" min={0} max={50} defaultValue={store.recurringDiscountPercent ?? 0}
                  className="w-16 border border-slate-200 rounded px-1.5 py-1 text-xs text-center"
                  onBlur={(e) => void patch(store.id, { recurringDiscountPercent: Math.max(0, Math.min(50, Number(e.target.value))) })} />
                <span className="text-xs text-slate-400">%</span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg bg-violet-50/60 border border-violet-100 px-2.5 py-2">
              <span className="text-sm font-semibold text-violet-700">Menu uplift</span>
              <div className="flex items-center gap-1">
                <input type="number" min={0} max={500} defaultValue={store.menuUpliftPercent ?? 0}
                  title="Markup on top of this shop's menu prices — customer pays it, hub keeps it. 0 = off."
                  className="w-16 border border-violet-200 rounded px-1.5 py-1 text-xs text-center"
                  onBlur={(e) => void patch(store.id, { menuUpliftPercent: Math.max(0, Math.min(500, Math.round(Number(e.target.value) || 0))) } as Partial<StoreRow>)} />
                <span className="text-xs text-slate-400">%</span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-slate-600">Stripe fee paid by</span>
              <select defaultValue={store.stripeFeeResponsibility ?? "shop"}
                className="border border-slate-200 rounded px-2 py-1 text-xs"
                onChange={(e) => void patch(store.id, { stripeFeeResponsibility: e.target.value } as Partial<StoreRow>)}>
                <option value="shop">Shop</option>
                <option value="hub">Ayrshire Hub</option>
              </select>
            </div>
          </div>
        </Section>

        <Section title="Device">
          <div className="flex items-center gap-2">
            <input type="text" defaultValue={store.rustdeskId ?? ""} placeholder="RustDesk Device ID"
              className="flex-1 border border-slate-200 rounded px-2 py-1.5 text-xs font-mono"
              onBlur={(e) => void patch(store.id, { rustdeskId: e.target.value.trim() || null } as Partial<StoreRow>)} />
            {store.rustdeskId && (
              <Button size="sm" variant="outline" className="h-8 text-xs gap-1 text-teal-700 border-teal-200 bg-teal-50 hover:bg-teal-100" onClick={() => { onRemote(); onClose(); }}>
                <Monitor size={12} /> Remote
              </Button>
            )}
          </div>
        </Section>

        <Section title="Features">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant={store.subcategoriesEnabled ? "secondary" : "outline"}
              className={`h-8 text-xs gap-1 ${store.subcategoriesEnabled ? "bg-violet-100 text-violet-700 border-violet-200 hover:bg-violet-200" : "text-violet-600 border-violet-200 hover:bg-violet-50"}`}
              onClick={() => void patch(store.id, { subcategoriesEnabled: !store.subcategoriesEnabled } as Partial<StoreRow>)}>
              🗂️ Sub-categories {store.subcategoriesEnabled ? "✓" : "Off"}
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1 text-teal-600 border-teal-200 hover:bg-teal-50" onClick={() => { onMultiStore(); onClose(); }}>
              🛍️ Multi-Store
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1 text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => { onBoost(); onClose(); }}>
              <Zap size={12} /> Boost
            </Button>
          </div>
        </Section>

        <Section title="Admin">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => { onTransfer(); onClose(); }}>
              <ArrowRightLeft size={12} /> Transfer owner
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => { onDelete(); onClose(); }}>
              <Trash2 size={12} /> Delete store
            </Button>
          </div>
        </Section>
      </div>
    </div>
  );
}

export default function AdminOrderStores() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [stores, setStores] = useState<StoreRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [transferOpenId, setTransferOpenId] = useState<number | null>(null);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [boostStoreId, setBoostStoreId] = useState<number | null>(null);
  const [multiStoreOpenId, setMultiStoreOpenId] = useState<number | null>(null);
  const [printerOpenId, setPrinterOpenId] = useState<number | null>(null);
  const [termsOpenId, setTermsOpenId] = useState<number | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [remoteStore, setRemoteStore] = useState<StoreRow | null>(null);
  const [settingsStoreId, setSettingsStoreId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/order-stores", { headers: authHeaders() });
      const d = await res.json() as { items: StoreRow[] };
      setStores(d.items ?? []);
    } catch { toast({ title: "Failed to load stores", variant: "destructive" }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  async function deleteStore(id: number) {
    setDeleting(true);
    try {
      const res = await fetch(`/api/admin/order-stores/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) {
        const d = await res.json() as { error?: string };
        toast({ title: d.error ?? "Failed to delete store", variant: "destructive" });
        return;
      }
      setStores((prev) => prev.filter((s) => s.id !== id));
      toast({ title: "Store deleted" });
    } catch { toast({ title: "Failed to delete store", variant: "destructive" }); }
    finally { setDeleting(false); setDeleteConfirmId(null); }
  }

  async function patch(id: number, updates: Partial<StoreRow>) {
    try {
      const res = await fetch(`/api/admin/order-stores/${id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify(updates),
      });
      const updated = await res.json() as StoreRow;
      setStores((prev) => prev.map((s) => (s.id === id ? { ...s, ...updated } : s)));
      toast({ title: "Store updated" });
    } catch { toast({ title: "Failed to update", variant: "destructive" }); }
  }

  const allTowns = [...new Set(stores.map(s => s.town).filter(Boolean))].sort();
  const boostStore = boostStoreId != null ? stores.find(s => s.id === boostStoreId) ?? null : null;

  const filtered = stores.filter((s) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return s.name.toLowerCase().includes(q) || s.town.toLowerCase().includes(q) || s.category.toLowerCase().includes(q);
  });

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  return (
    <>
    <AdminLayout current="/admin/order-stores">
      <div className="p-6 space-y-6">
        {showCreatePanel && (
          <CreateShopPanel stores={stores} onCreated={() => void load()} onClose={() => setShowCreatePanel(false)} />
        )}
        {boostStore && (
          <BoostModal
            store={boostStore}
            allTowns={allTowns}
            onClose={() => setBoostStoreId(null)}
            onBoostCreated={() => setBoostStoreId(null)}
          />
        )}
        {showEmailModal && (
          <BroadcastEmailModal
            emails={stores.filter(s => s.isActive && s.ownerEmail).map(s => s.ownerEmail!)}
            onClose={() => setShowEmailModal(false)}
          />
        )}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Order Stores</h1>
            <p className="text-sm text-slate-500 mt-1">Approve, activate and fully manage shops on the Order Local platform</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              onClick={() => setShowEmailModal(true)}
            >
              <Mail size={14} /> Email Owners
            </Button>
            <Button
              size="sm"
              className="bg-primary hover:bg-primary/90 gap-1.5"
              onClick={() => setShowCreatePanel(true)}
            >
              <UserPlus size={14} /> Create Shop
            </Button>
            <Badge variant="secondary">{stores.length} stores</Badge>
          </div>
        </div>

        <div className="relative max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search stores…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 text-sm"
          />
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <Store size={40} className="mx-auto mb-3 opacity-30" />
            <p>{search ? "No stores match your search" : "No stores registered yet"}</p>
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-2xl border border-slate-100 shadow-sm">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-5 py-3 text-slate-500 font-semibold">Store</th>
                  <th className="text-left px-5 py-3 text-slate-500 font-semibold">Category</th>
                  <th className="text-left px-5 py-3 text-slate-500 font-semibold">Town</th>
                  <th className="text-left px-5 py-3 text-slate-500 font-semibold">Payment</th>
                  <th className="text-left px-5 py-3 text-slate-500 font-semibold">Status</th>
                  <th className="text-left px-5 py-3 text-slate-500 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((store) => (
                  <Fragment key={store.id}>
                  <tr className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3">
                      <div className="font-semibold text-foreground">{store.name}</div>
                      <div className="text-xs text-slate-400">{store.address}</div>
                      {store.phone && <div className="text-xs text-slate-400">{store.phone}</div>}
                      {store.ownerName && <div className="text-xs text-slate-400 mt-0.5">Owner: <span className="font-medium text-slate-600">{store.ownerName}</span></div>}
                      <InlineSlugEditor store={store} onSave={(slug) => setStores(prev => prev.map(s => s.id === store.id ? { ...s, slug } : s))} />
                    </td>
                    <td className="px-5 py-3 text-slate-600">
                      <div>{store.categories?.length ? store.categories.join(", ") : store.category}</div>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{store.town}</td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col gap-1">
                        {store.stripeOnboardingComplete ? (
                          <Badge className="bg-emerald-100 text-emerald-700 w-fit">Stripe ✓</Badge>
                        ) : store.stripeAccountId ? (
                          // Connected to Stripe but charges are not enabled — this store
                          // CANNOT take card payments. Called out separately from "never
                          // connected" because it looks fine to the shop owner.
                          <Badge
                            className="bg-red-100 text-red-700 w-fit"
                            title="Stripe account connected but charges are not enabled — this store cannot take card payments"
                          >
                            Stripe not enabled
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="w-fit">No Stripe</Badge>
                        )}
                        {store.acceptsCash && (
                          <Badge className="bg-amber-100 text-amber-700 w-fit">Cash ✓</Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex gap-1 flex-wrap">
                        <Badge className={store.approved ? "bg-primary text-white" : "bg-slate-100 text-slate-500"}>
                          {store.approved ? "Approved" : "Pending"}
                        </Badge>
                        <Badge className={store.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>
                          {store.isActive ? "Live" : "Inactive"}
                        </Badge>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        {!store.approved ? (
                          <Button
                            size="sm"
                            className="bg-primary hover:bg-primary/90 h-8 text-xs"
                            onClick={() => void patch(store.id, { approved: true, isActive: true })}
                          >
                            <CheckCircle size={12} className="mr-1" /> Approve
                          </Button>
                        ) : store.isActive ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs border-red-200 text-red-600 hover:bg-red-50"
                            onClick={() => void patch(store.id, { isActive: false })}
                          >
                            <XCircle size={12} className="mr-1" /> Deactivate
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs"
                            onClick={() => void patch(store.id, { isActive: true })}
                          >
                            Activate
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs gap-1"
                          onClick={() => navigate(`/business/order-store/${store.id}`)}
                        >
                          <Store size={12} /> Menu
                        </Button>

                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs gap-1 text-slate-600"
                          onClick={() => setSettingsStoreId(store.id)}
                        >
                          <Settings size={12} /> Settings
                        </Button>

                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs gap-1 text-slate-600"
                          onClick={() => setTermsOpenId(termsOpenId === store.id ? null : store.id)}
                        >
                          <FileText size={12} /> Terms
                        </Button>

                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setPrinterOpenId(printerOpenId === store.id ? null : store.id)}>Printer Management</Button>
                        {store.slug && (
                          <a href={`/order/${store.slug}`} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="ghost" className="h-8 text-xs text-slate-400 hover:text-primary gap-1">
                              <ExternalLink size={12} /> View
                            </Button>
                          </a>
                        )}

                        {/* Delete confirmation appears here when triggered from the Settings modal. */}
                        {deleteConfirmId === store.id && (
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              className="h-8 text-xs bg-red-600 hover:bg-red-700 gap-1"
                              disabled={deleting}
                              onClick={() => void deleteStore(store.id)}
                            >
                              <Trash2 size={12} /> {deleting ? "Deleting…" : "Confirm delete"}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 text-xs"
                              onClick={() => setDeleteConfirmId(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                  {printerOpenId === store.id && <tr><td colSpan={6} className="p-4"><PrinterManagementPanel storeId={store.id}/></td></tr>}
                  {termsOpenId === store.id && (
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <td colSpan={6} className="px-5 py-3">
                        <TermsAcceptancePanel storeId={store.id} storeName={store.name} />
                      </td>
                    </tr>
                  )}
                  {transferOpenId === store.id && (
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <td colSpan={6} className="px-5 py-3">
                        <TransferOwnerPanel
                          store={store}
                          onTransferred={(newOwnerId, name, email) => {
                            setStores(prev => prev.map(s => s.id === store.id ? { ...s, ownerId: newOwnerId, ownerName: name, ownerEmail: email } : s));
                            setTransferOpenId(null);
                          }}
                        />
                      </td>
                    </tr>
                  )}
                  {multiStoreOpenId === store.id && (
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <td colSpan={6} className="px-5 py-3">
                        <MultiStorePanel
                          store={store}
                          onSaved={(updates) => {
                            setStores(prev => prev.map(s => s.id === store.id ? { ...s, ...updates } : s));
                          }}
                          onClose={() => setMultiStoreOpenId(null)}
                        />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
    {remoteStore && <RemoteControlModal store={remoteStore} onClose={() => setRemoteStore(null)} />}
    {settingsStoreId != null && (() => {
      const s = stores.find(st => st.id === settingsStoreId);
      return s ? (
        <StoreSettingsModal
          store={s}
          onClose={() => setSettingsStoreId(null)}
          patch={patch}
          onBoost={() => { setBoostStoreId(s.id); setTransferOpenId(null); setDeleteConfirmId(null); setMultiStoreOpenId(null); }}
          onMultiStore={() => { setMultiStoreOpenId(s.id); setTransferOpenId(null); setDeleteConfirmId(null); }}
          onTransfer={() => { setTransferOpenId(s.id); setMultiStoreOpenId(null); }}
          onRemote={() => setRemoteStore(s)}
          onDelete={() => setDeleteConfirmId(s.id)}
        />
      ) : null;
    })()}
    </>
  );
}
